import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { muscleGroups } from "../data/muscleGroups";
import { getWeekdayLabel, getMonthDayLabel, formatPromptDateLabel } from "../utils/formatDateLabels";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";
import { todayYMDLocal, addDaysYMDLocal } from "../utils/dateUtils";
import { calculateDayVolume, calculateDayCardioMinutes, formatVolumeLoad, formatDualVolume } from "../utils/volumeLoad";
import DatePromptModal from "./DatePromptModal";

const buildExerciseLookup = () => Object.fromEntries(getAllExercisesCatalog().map((item) => [item.id, item]));

const truncateToTwoWords = (text) => {
    if (typeof text !== "string") {
        return text;
    }

    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) {
        return text;
    }

    return `${words.slice(0, 2).join(" ")}...`;
};

const getMuscleImageFilename = (groupId) => {
    const overrides = {
        hip_flexors: "Hip_Flexor",
    };

    const baseName = overrides[groupId]
        ? overrides[groupId]
        : groupId
            .split("_")
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join("_");

    return `${baseName}-54.png`;
};

const getMuscleImageWebpFilename = (groupId) => getMuscleImageFilename(groupId).replace(".png", ".webp");

function collectExercises({ date, groupId, exerciseLog, exerciseMap, t }) {
    return Object.entries(exerciseLog[date] || {})
        .reduce((acc, [exerciseId]) => {
            const definition = exerciseMap[exerciseId];
            const groupImpact = definition?.muscleGroups.find((mg) => mg.group === groupId);

            if (!groupImpact) {
                return acc;
            }

            const fallbackName = t ? t("home.unknownExercise", { id: exerciseId }) : `Unknown (${exerciseId})`;
            acc.push({
                id: exerciseId,
                date,
                name: definition ? getLocalizedExerciseName(definition, t) : fallbackName,
                priority: groupImpact.priority || 'primary',
            });

            return acc;
        }, []);
}

function ExerciseLinks({ exercises, localizePath, onAddExercise, t }) {
    if (exercises.length === 0) {
        return null;
    }

    return (
        <div className="exerciseGroup">
            {exercises.map(({ id, date, name, priority }) => (
                <div key={`${id}-${date}`} className="exerciseRow">
                    <Link
                        to={localizePath(`/LogExercise/${id}/${date}`)}
                        className={`exerciseLink${priority === 'secondary' ? ' exerciseLink--indirect' : ''}`}
                    >
                        <span>{truncateToTwoWords(name)}</span>
                    </Link>
                    <button
                        type="button"
                        onClick={() => onAddExercise({ id, name, date })}
                        className="addExerciseButton addExerciseButton--muscleGroup addExerciseButton--inlineExercise"
                        data-exercise-id={id}
                        data-date={date}
                        aria-label={t ? t("home.addExerciseForToday", { name }) : `Add ${name} for today`}
                        title={t ? t("home.addExerciseForToday", { name }) : `Add ${name} for today`}
                    >
                        <span aria-hidden="true">⧉</span>
                    </button>
                </div>
            ))}
        </div>
    );
}

export default function WorkoutProgressTable({
    dates = [],
    exerciseLog = {},
    workoutNotes = {},
    onOpenNoteModal,
    showIndirect = true,
    dateIndex: propDateIndex,
    onDateIndexChange,
}) {
    const { t, locale, localizePath } = useI18n();
    const navigate = useNavigate();
    const exerciseMap = useMemo(buildExerciseLookup, []);
    const [internalDateIndex, setInternalDateIndex] = useState(0);
    const dateIndex = propDateIndex !== undefined ? propDateIndex : internalDateIndex;
    const setDateIndex = onDateIndexChange || setInternalDateIndex;

    const [stickyTop, setStickyTop] = useState(54);
    const [datePrompt, setDatePrompt] = useState({
        isOpen: false,
        type: null,
        targetId: "",
        targetName: "",
        targetDate: "",
        selectedChoice: "today",
    });

    const handleCloseDatePrompt = () => {
        setDatePrompt((prev) => ({ ...prev, isOpen: false }));
    };

    const handleConfirmDatePrompt = () => {
        const isTodayChoice = datePrompt.selectedChoice === "today";
        const chosenDate = isTodayChoice ? todayDate : datePrompt.targetDate;

        if (datePrompt.type === "exercise") {
            navigate(localizePath(`/LogExercise/${datePrompt.targetId}/${chosenDate}`));
        } else if (datePrompt.type === "muscleGroup") {
            if (isTodayChoice) {
                navigate(localizePath(`/SelectExercise/${datePrompt.targetId}`));
            } else {
                navigate(localizePath(`/SelectExercise/${datePrompt.targetId}?date=${chosenDate}`));
            }
        }

        setDatePrompt((prev) => ({ ...prev, isOpen: false }));
    };

    const handleExercisePlusClick = ({ id, name, date }) => {
        if (date === todayDate) {
            navigate(localizePath(`/LogExercise/${id}/${todayDate}`));
        } else {
            setDatePrompt({
                isOpen: true,
                type: "exercise",
                targetId: id,
                targetName: name,
                targetDate: date,
                selectedChoice: "today",
            });
        }
    };

    const handleMuscleGroupPlusClick = (group, date) => {
        if (date === todayDate) {
            navigate(localizePath(`/SelectExercise/${group.id}`));
        } else {
            setDatePrompt({
                isOpen: true,
                type: "muscleGroup",
                targetId: group.id,
                targetName: t(`muscleGroups.${group.id}`) || group.name,
                targetDate: date,
                selectedChoice: "today",
            });
        }
    };

    useEffect(() => {
        const updateStickyTop = () => {
            const breadcrumbs = document.querySelector(".breadcrumbs");
            if (breadcrumbs) {
                setStickyTop(Math.round(breadcrumbs.offsetHeight));
            }
        };

        updateStickyTop();
        window.addEventListener("resize", updateStickyTop);
        return () => window.removeEventListener("resize", updateStickyTop);
    }, []);

    const todayDate = todayYMDLocal();
    const safeDates = Array.isArray(dates) ? dates : [];

    // Dynamically extend dates into the past if user navigates beyond initial dates
    const paddedDates = useMemo(() => {
        const result = [...safeDates];
        if (result.length === 0) {
            result.push(todayDate);
        }
        let back = 1;
        while (result.length < dateIndex + 4 && safeDates.length >= 4) {
            const lastDate = result[result.length - 1];
            const fallback = addDaysYMDLocal(lastDate, -1);
            if (!result.includes(fallback)) {
                result.push(fallback);
            } else {
                result.push(addDaysYMDLocal(todayDate, -back));
            }
            back++;
        }
        return result;
    }, [safeDates, dateIndex, todayDate]);

    const activeDateIndex = useMemo(() => {
        if (paddedDates.length === 0) return 0;
        return Math.min(Math.max(0, dateIndex), paddedDates.length - 1);
    }, [paddedDates.length, dateIndex]);

    const todayIndex = useMemo(() => {
        return paddedDates.findIndex((d) => d === todayDate);
    }, [paddedDates, todayDate]);

    const isTodayActive = todayIndex !== -1 ? activeDateIndex === todayIndex : activeDateIndex === 0;

    const handleGoToToday = () => {
        if (!isTodayActive) {
            setDateIndex(todayIndex !== -1 ? todayIndex : 0);
        }
    };

    const canGoPrev = true;
    const canGoNext = activeDateIndex > 0;

    const handlePrevDay = () => {
        setDateIndex(activeDateIndex + 1);
    };

    const handleNextDay = () => {
        if (canGoNext) {
            setDateIndex(activeDateIndex - 1);
        }
    };

    // Show up to 4 dates on desktop; on mobile only the first is shown via CSS
    const visibleDates = useMemo(() => {
        const count = Math.min(4, Math.max(1, paddedDates.length - activeDateIndex));
        return paddedDates.slice(activeDateIndex, activeDateIndex + count);
    }, [paddedDates, activeDateIndex]);

    const columns = useMemo(
        () =>
            visibleDates.map((date, index) => ({
                key: date,
                weekday: getWeekdayLabel(date, locale),
                monthDay: getMonthDayLabel(date, locale),
                isMobileActive: index === 0,
                isDesktopOnly: index > 0,
                isToday: date === todayDate,
            })),
        [visibleDates, locale, todayDate]
    );

    return (
        <>
        <table
            className="muscleTable"
            aria-label={t("home.workoutTracker")}
            style={{ "--sticky-header-top": `${stickyTop}px` }}
        >
            <thead>
            <tr>
                <th className="muscleGroupHeader">{t("home.muscleGroup")}</th>
                {columns.map(({ key, weekday, monthDay, isMobileActive, isDesktopOnly, isToday }, index) => {
                    return (
                        <th
                            key={key}
                            className={`dateHeader${isToday ? " dateHeader--today" : ""}${isMobileActive ? " dateHeader--mobileActive" : ""}${isDesktopOnly ? " dateHeader--desktopOnly" : ""}`}
                        >
                            <div className="dateHeaderDesktop">
                                <div className="dateHeaderTitleGroup">
                                    {isToday && (
                                        <span className="dateHeaderTodayBadge">{t("home.today")}</span>
                                    )}
                                    <div className="dateHeaderWeekday">{weekday}</div>
                                    <div className="dateHeaderDate">{monthDay}</div>
                                </div>
                            </div>
                            {isMobileActive && (
                                <div className="dateHeaderMobileNav">
                                    <button
                                        type="button"
                                        className={`dayNavButton dayNavButton--prev${canGoPrev ? " dayNavButton--active" : " dayNavButton--inactive"}`}
                                        onClick={handlePrevDay}
                                        disabled={!canGoPrev}
                                        aria-disabled={!canGoPrev}
                                        aria-label={t("home.prevDay")}
                                        title={t("home.prevDay")}
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                            focusable="false"
                                        >
                                            <polyline points="15 18 9 12 15 6" />
                                        </svg>
                                    </button>
                                    <div className="dateHeaderMobileDateInfo">
                                        {isToday && (
                                            <span className="dateHeaderTodayBadge">{t("home.today")}</span>
                                        )}
                                        <div className="dateHeaderWeekday">{weekday}</div>
                                        <div className="dateHeaderDate">{monthDay}</div>
                                        {!isTodayActive && (
                                            <button
                                                type="button"
                                                className="todayNavButton todayNavButton--active"
                                                onClick={handleGoToToday}
                                                aria-label={t("home.goToToday")}
                                                title={t("home.goToToday")}
                                            >
                                                <span>{t("home.goToToday")}</span>
                                                <svg
                                                    className="todayNavButtonArrowIcon"
                                                    width="11"
                                                    height="11"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                >
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={`dayNavButton dayNavButton--next${canGoNext ? " dayNavButton--active" : " dayNavButton--inactive"}`}
                                        onClick={handleNextDay}
                                        disabled={!canGoNext}
                                        aria-disabled={!canGoNext}
                                        aria-label={t("home.nextDay")}
                                        title={t("home.nextDay")}
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                            focusable="false"
                                        >
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </th>
                    );
                })}
            </tr>
            </thead>
            <tbody>
                        <tr className="volumeLoadProgressRow">
                <td className="muscleGroupCell volumeLoadHeaderCell" aria-hidden="true">
                </td>
                {columns.map(({ key, isMobileActive, isDesktopOnly, isToday }) => {
                    const dayVolume = calculateDayVolume(exerciseLog[key]);
                    const dayCardio = calculateDayCardioMinutes(exerciseLog[key]);
                    const note = workoutNotes?.[key];
                    const hasNote = typeof note === "string" && note.trim().length > 0;
                    const cardioUnit = t("home.cardioUnit");

                    let volumeText = formatVolumeLoad(0, locale);
                    if (dayVolume > 0 && dayCardio > 0) {
                        volumeText = `${t("home.volumeLoad")}: ${formatDualVolume(dayVolume, dayCardio, locale, { cardioUnit })}`;
                    } else if (dayVolume > 0) {
                        volumeText = `${t("home.volumeLoad")}: ${formatVolumeLoad(dayVolume, locale)}`;
                    } else if (dayCardio > 0) {
                        volumeText = formatDualVolume(0, dayCardio, locale, { cardioUnit });
                    }

                    return (
                        <td
                            key={`volume-${key}`}
                            data-date={key}
                            className={`progressCell volumeLoadCell${isToday ? " progressCell--today" : ""}${isMobileActive ? " progressCell--mobileActive" : ""}${isDesktopOnly ? " progressCell--desktopOnly" : ""}`}
                        >
                            <div className="progressCellContent volumeLoadCellContent">
                                <span className="volumeLoadBadge">
                                    {volumeText}
                                </span>
                                <div className="dateNoteBlock">
                                    {hasNote ? (
                                        <button
                                            type="button"
                                            className="noteBadgeButton noteBadgeButton--hasNote"
                                            onClick={() => onOpenNoteModal && onOpenNoteModal({ date: key, note })}
                                            data-date={key}
                                            title={note}
                                            aria-label={t("home.editNoteForDate", { date: key })}
                                        >
                                            <span className="noteBadgeIcon" aria-hidden="true">📝</span>
                                            <span className="noteBadgeText">{truncateToTwoWords(note)}</span>
                                            <span className="noteBadgeActionLabel">{t("home.editNote")}</span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="noteBadgeButton noteBadgeButton--empty"
                                            onClick={() => onOpenNoteModal && onOpenNoteModal({ date: key, note: "" })}
                                            data-date={key}
                                            aria-label={t("home.addNoteForDate", { date: key })}
                                            title={t("home.addNote")}
                                        >
                                            <span className="noteAddPlus" aria-hidden="true">+</span>
                                            <span className="noteBadgeActionLabel">{t("home.addNote")}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </td>
                    );
                })}
            </tr>
            {muscleGroups.map((group) => (
                <tr key={group.id}>
                    <td className="muscleGroupCell">
                        <div className="muscleGroupInfoLayout">
                            <div className="muscleGroupIconCol">
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={`/images/muscles/${getMuscleImageWebpFilename(group.id)} 1x, /images/muscles/${getMuscleImageWebpFilename(group.id).replace('-54.webp', '-108.webp')} 2x`}
                                    />
                                    <img
                                        src={`/images/muscles/${getMuscleImageFilename(group.id)}`}
                                        srcSet={`/images/muscles/${getMuscleImageFilename(group.id)} 1x, /images/muscles/${getMuscleImageFilename(group.id).replace('-54.png', '-108.png')} 2x`}
                                        alt={t(`muscleGroups.${group.id}`) || group.name}
                                        className="muscleGroupImage"
                                        loading="lazy"
                                        width="54"
                                        height="54"
                                    />
                                </picture>
                            </div>
                            <div className="muscleGroupTextCol">
                                <span className="muscleGroupName">
                                    {t(`muscleGroups.${group.id}`) || group.name}
                                </span>
                            </div>
                        </div>
                    </td>
                    {columns.map(({ key, isMobileActive, isDesktopOnly, isToday }) => {
                        const exercises = collectExercises({
                            date: key,
                            groupId: group.id,
                            exerciseLog,
                            exerciseMap,
                            t,
                        });
                        const filtered = showIndirect ? exercises : exercises.filter((e) => e.priority !== 'secondary');

                        return (
                            <td
                                key={`${group.id}-${key}`}
                                data-group={group.id}
                                data-date={key}
                                className={`progressCell${isToday ? " progressCell--today" : ""}${isMobileActive ? " progressCell--mobileActive" : ""}${isDesktopOnly ? " progressCell--desktopOnly" : ""}`}
                            >
                                <div className="progressCellContent">
                                    <ExerciseLinks
                                        exercises={filtered}
                                        localizePath={localizePath}
                                        onAddExercise={handleExercisePlusClick}
                                        t={t}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleMuscleGroupPlusClick(group, key)}
                                        className="addExerciseButton addExerciseButton--muscleGroup"
                                        data-group={group.id}
                                        data-date={key}
                                        aria-label={t("home.addExerciseFor", {
                                            group: t(`muscleGroups.${group.id}`) || group.name,
                                        })}
                                        title={t("home.addExerciseFor", {
                                            group: t(`muscleGroups.${group.id}`) || group.name,
                                        })}
                                    >
                                        <span aria-hidden="true">+</span>
                                    </button>
                                </div>
                            </td>
                        );
                    })}
                </tr>
            ))}
            </tbody>
        </table>
        <DatePromptModal
            isOpen={datePrompt.isOpen}
            type={datePrompt.type}
            targetName={datePrompt.targetName}
            targetDate={datePrompt.targetDate}
            todayDate={todayDate}
            selectedChoice={datePrompt.selectedChoice}
            onChoiceChange={(choice) => setDatePrompt((prev) => ({ ...prev, selectedChoice: choice }))}
            onConfirm={handleConfirmDatePrompt}
            onClose={handleCloseDatePrompt}
        />
        </>
    );
}
